import React, { useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import DOMPurify from 'dompurify';
import { useFormPalette } from './DynamicFormPrimitives';
import MarkdownContent from '../MarkdownContent';
import { TipTapEditor } from '../../TipTap';
import { FileTile } from '../AttachmentStrip';
import { styled } from '@mui/material/styles';
import {
  AnimatedFieldWrapper,
  StyledTextField,
  StyledSelectField,
  StyledRadioOption,
  StyledCheckboxOption,
  StyledRadio,
  StyledCheckbox,
  StyledUploadZone,
  FileChip,
  GhostAddButton,
  shake,
} from './PublicFormStyles';

function normalizeFields(form) {
  if (!form?.fields || !Array.isArray(form.fields)) return [];
  return [...form.fields].sort((a, b) => (a.order || 0) - (b.order || 0));
}

function toArray(value) {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
}

// The form builder only exposes `label` to users. The synthetic `value` field
// is auto-generated (slugified, truncated, sometimes collided — e.g. multiple
// options ending up with value "1" or "h") and never surfaced in the builder
// UI. Submitting that hidden value leaks machine keys like `id_clip_holder`
// into emails / Run Code output. Normalize so the submitted value is always
// the label the user actually configured.
function dropdownOptions(rawOptions) {
  const opts = Array.isArray(rawOptions) ? rawOptions : [];
  const seen = new Set();
  return opts.map((o, i) => {
    const rawLabel = o?.label;
    const rawValue = o?.value;
    const hasLabel = rawLabel != null && String(rawLabel) !== '';
    const base = hasLabel
      ? String(rawLabel)
      : (rawValue != null && String(rawValue) !== '' ? String(rawValue) : `__opt_${i}`);
    let v = base;
    let n = 2;
    while (seen.has(v)) { v = `${base} (${n++})`; }
    seen.add(v);
    return { ...o, value: v, label: hasLabel ? String(rawLabel) : v };
  });
}

function fileAttachment(file, idx) {
  const id = file?.id ?? file?.fileId ?? `idx-${idx}`;
  return {
    ...file,
    id,
    fileName: file?.name || file?.fileName || `File ${idx + 1}`,
  };
}

function valueForField(field, answers) {
  const fieldType = String(field?.type || '');
  if (fieldType === 'display') return '';
  if (fieldType === 'checkbox') return toArray(answers?.[field.key]);
  if (fieldType === 'list') return toArray(answers?.[field.key]);
  if (fieldType === 'repeatable_section') {
    const rows = answers?.[field.key];
    if (Array.isArray(rows) && rows.length > 0) return rows;
    return [{}];
  }
  if (fieldType.endsWith('_upload')) return toArray(answers?.[field.key]);
  const raw = answers?.[field.key];
  if ((raw === undefined || raw === null || raw === '') && field.defaultValue) {
    return field.defaultValue;
  }
  return raw ?? '';
}

function UploadZone({
  palette,
  inputId,
  isUploading,
  isMaxed,
  multiple,
  accept,
  iconSize,
  fontSize,
  maxedLabel,
  onFiles,
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = React.useState(false);
  const disabled = isUploading || isMaxed;

  const openPicker = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPicker();
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (disabled) return;
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length > 0) onFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    if (!dragOver) setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  return (
    <StyledUploadZone
      palette={palette}
      isDragOver={dragOver}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      aria-label={isMaxed ? maxedLabel : 'Drag and drop a file or press Enter to browse'}
      onClick={openPicker}
      onKeyDown={handleKeyDown}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragEnter={handleDragOver}
      onDragLeave={handleDragLeave}
      sx={{
        opacity: disabled && !isUploading ? 0.6 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        '&:focus-visible': {
          outline: `2px solid ${palette.textSecondary}`,
          outlineOffset: 2,
        },
      }}
    >
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        style={{ display: 'none' }}
        multiple={multiple}
        accept={accept}
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length > 0) onFiles(files);
          e.target.value = '';
        }}
      />
      {isUploading ? (
        <CircularProgress size={iconSize - 2} thickness={5} sx={{ color: palette.textSecondary }} />
      ) : (
        <CloudUploadOutlinedIcon sx={{ width: iconSize, height: iconSize, color: palette.textSecondary }} />
      )}
      <Typography sx={{ fontSize, color: palette.textSecondary }}>
        {isUploading ? 'Uploading...' : isMaxed ? maxedLabel : 'Drag & drop or click to upload'}
      </Typography>
    </StyledUploadZone>
  );
}

const RichTextEditorWrapper = styled('div')(({ theme }) => ({
  width: '100%',
  '& .tiptap-editor-container': {
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
    borderRadius: '10px',
    overflow: 'hidden',
    backgroundColor: 'transparent',
    transition: 'border-color 200ms ease, box-shadow 200ms ease',
  },
  '& .tiptap-editor-container:focus-within': {
    borderColor: theme.palette.mode === 'dark' ? '#fafafa' : '#18181b',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 0 0 3px rgba(250,250,250,0.08)'
      : '0 0 0 3px rgba(24,24,27,0.08)',
  },
  '& .tiptap-editor-content': {
    padding: '0 !important',
    minHeight: 'auto !important',
  },
  '& .ProseMirror': {
    fontSize: '15px',
    minHeight: '120px',
    padding: '12px 14px',
    outline: 'none',
    border: 'none',
    lineHeight: 1.6,
    '& p': { margin: 0 },
    '& p + p': { marginTop: '8px' },
  },
}));

const ClassicRichTextEditorWrapper = styled('div')(({ theme }) => ({
  width: '100%',
  '& .tiptap-editor-container': {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '6px',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default,
  },
  '& .tiptap-editor-container:focus-within': {
    borderColor: theme.palette.text.secondary,
  },
  '& .tiptap-editor-content': {
    padding: '0 !important',
    minHeight: 'auto !important',
  },
  '& .ProseMirror': {
    fontSize: '13px',
    minHeight: '120px',
    padding: '10px 12px',
    outline: 'none',
    border: 'none',
    '& p': { margin: 0 },
    '& p + p': { marginTop: '6px' },
  },
}));

function looksLikeHtml(content) {
  if (typeof content !== 'string') return false;
  return /<\/?[a-z][\s\S]*>/i.test(content);
}

function resolveDisplayContentFormat(field, content) {
  const rawFormat = String(field?.contentFormat || '').trim().toLowerCase();
  if (rawFormat === 'markdown' || rawFormat === 'html') return rawFormat;
  return looksLikeHtml(content) ? 'html' : 'markdown';
}

function getDisplayContentSx(palette, isDark, publicMode) {
  return {
    fontSize: publicMode ? '14px' : '12px',
    lineHeight: publicMode ? 1.6 : 1.5,
    color: palette.textPrimary,
    '& p': { m: 0 },
    '& p + p': { mt: publicMode ? 1 : 0.75 },
    '& ul, & ol': { mt: 0.5, mb: 0.5, pl: 2.5 },
    '& code': {
      fontFamily: '"SF Mono", SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
      fontSize: publicMode ? '13px' : '11px',
      backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
      borderRadius: '4px',
      px: 0.5,
      py: 0.15,
    },
    '& a': {
      color: publicMode ? palette.textPrimary : (isDark ? '#818cf8' : '#4f46e5'),
      textDecoration: publicMode ? 'underline' : 'none',
      ...(publicMode
        ? { '&:hover': { opacity: 0.7 } }
        : {
            borderBottom: '1px solid transparent',
            '&:hover': { borderBottom: `1px solid ${isDark ? '#818cf8' : '#4f46e5'}` },
          }),
    },
  };
}

// ── Display content helper ──

function DisplayContent({ field, displayContentSx }) {
  const rawContent = field.content;
  const content = typeof rawContent === 'string' ? rawContent : rawContent == null ? '' : String(rawContent);
  if (!content.trim()) return null;
  const format = resolveDisplayContentFormat(field, content);
  if (format === 'html') {
    const safeHtml = DOMPurify.sanitize(content, { USE_PROFILES: { html: true } });
    return <Box sx={displayContentSx} dangerouslySetInnerHTML={{ __html: safeHtml }} />;
  }
  return <MarkdownContent sx={displayContentSx}>{content}</MarkdownContent>;
}

// CheckIcon removed — MUI Checkbox provides its own check icon

// ── Field label (public mode) ──

function PublicFieldLabel({ label, required, isDisplay, palette }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 1 }}>
      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: palette.textPrimary }}>{label}</Typography>
      {!isDisplay && required && (
        <Typography sx={{ fontSize: '12px', color: palette.textSecondary, fontWeight: 400 }}>(required)</Typography>
      )}
    </Box>
  );
}

// ── Field label (classic mode) ──

function ClassicFieldLabel({ label, required, isDisplay, palette }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: palette.textPrimary }}>{label}</Typography>
      {!isDisplay && required && (
        <Typography sx={{ fontSize: '11px', color: palette.errorText }}>*</Typography>
      )}
    </Box>
  );
}

// ── Feedback text (public mode) ──

function PublicFieldFeedback({ err, warn, helpText, palette, isDark }) {
  if (!err && !warn && !helpText) return null;
  return (
    <Box sx={{ mt: 0.75 }}>
      {err && (
        <Typography sx={{ fontSize: '13px', color: isDark ? '#fca5a5' : '#b91c1c', fontWeight: 500, lineHeight: 1.4, animation: `${shake} 300ms ease` }}>
          {err}
        </Typography>
      )}
      {!err && warn && (
        <Typography sx={{ fontSize: '13px', color: isDark ? '#fbbf24' : '#92400e', fontWeight: 500, lineHeight: 1.4 }}>
          {warn}
        </Typography>
      )}
      {!err && !warn && helpText && (
        <Typography sx={{ fontSize: '13px', color: palette.textSecondary, lineHeight: 1.4 }}>{helpText}</Typography>
      )}
    </Box>
  );
}

// ── Feedback text (classic mode) ──

function ClassicFieldFeedback({ err, warn, helpText, label, palette, isDark }) {
  if (!err && !warn && !helpText) return null;
  return (
    <Box sx={{ mt: 0.65 }}>
      {err && (
        <Typography sx={{ fontSize: '11px', color: isDark ? '#fca5a5' : '#991b1b', fontWeight: 600, lineHeight: 1.35 }}>
          {label}: {err}
        </Typography>
      )}
      {!err && warn && (
        <Typography sx={{ fontSize: '11px', color: isDark ? '#fbbf24' : '#92400e', fontWeight: 600, lineHeight: 1.35 }}>
          {label}: {warn}
        </Typography>
      )}
      {!err && !warn && helpText && (
        <Typography sx={{ fontSize: '11px', color: palette.textSecondary }}>{helpText}</Typography>
      )}
    </Box>
  );
}

// ── Prefill chip (public mode) ──

function PublicPrefillChip({ palette }) {
  return (
    <Tooltip title="This field was pre-filled by an AI agent" arrow placement="top">
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          px: 1,
          py: 0.25,
          borderRadius: '4px',
          bgcolor: palette.chipBg,
          border: `1px solid ${palette.chipBorder}`,
          mb: 0.5,
        }}
      >
        <AutoAwesomeOutlinedIcon sx={{ width: 12, height: 12, color: palette.chipText }} />
        <Typography sx={{ fontSize: '11px', fontWeight: 500, color: palette.chipText }}>Filled by Agent</Typography>
      </Box>
    </Tooltip>
  );
}

// ── Classic prefill chip ──

function ClassicPrefillChip({ isDark }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.35 }}>
      <Chip
        size="small"
        label="Filled by Agent"
        sx={{
          height: '16px',
          borderRadius: '4px',
          border: `1px solid ${isDark ? 'rgba(124,58,237,0.3)' : '#ddd6fe'}`,
          backgroundColor: isDark ? 'rgba(124,58,237,0.15)' : '#f5f3ff',
          color: isDark ? '#c4b5fd' : '#6d28d9',
          '& .MuiChip-label': { px: 0.75, fontSize: '9px', fontWeight: 600, lineHeight: 1 },
        }}
      />
    </Box>
  );
}

// ══════════════════════════════════════════
// LIST FIELD
// ══════════════════════════════════════════

function ListFieldRenderer({ fieldKey, value, onChange, onFieldBlur, readOnly, placeholder, palette, hasError, publicMode }) {
  const items = Array.isArray(value) ? value : [];
  const inputRef = useRef(null);

  const handleAdd = useCallback(() => {
    const input = inputRef.current;
    const text = (input?.value || '').trim();
    if (!text) return;
    const next = [...items, text];
    onChange(fieldKey, next);
    onFieldBlur?.(fieldKey, next);
    input.value = '';
  }, [items, fieldKey, onChange, onFieldBlur]);

  const handleRemove = useCallback(
    (idx) => {
      const next = items.filter((_, i) => i !== idx);
      onChange(fieldKey, next);
      onFieldBlur?.(fieldKey, next);
    },
    [items, fieldKey, onChange, onFieldBlur]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAdd();
      }
    },
    [handleAdd]
  );

  if (publicMode) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {items.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {items.map((item, idx) => (
              <FileChip
                key={idx}
                palette={palette}
                label={item}
                onDelete={!readOnly ? () => handleRemove(idx) : undefined}
                deleteIcon={<CloseIcon sx={{ width: 14, height: 14, color: palette.textSecondary, '&:hover': { color: palette.textPrimary } }} />}
              />
            ))}
          </Box>
        )}
        {!readOnly && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <StyledTextField
              inputRef={inputRef}
              palette={palette}
              hasError={hasError}
              placeholder={placeholder || 'Type and press Enter to add...'}
              onKeyDown={handleKeyDown}
              variant="outlined"
            />
            <GhostAddButton palette={palette} onClick={handleAdd} sx={{ width: 'auto', minWidth: '64px', padding: '0 16px' }}>
              <AddIcon sx={{ width: 16, height: 16 }} />
              Add
            </GhostAddButton>
          </Box>
        )}
      </Box>
    );
  }

  // Classic mode
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {items.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {items.map((item, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, '&:hover .list-remove-btn': { opacity: 1 } }}>
              <Typography sx={{ fontSize: '11px', color: palette.textSecondary, width: 20, textAlign: 'right', flexShrink: 0 }}>{idx + 1}.</Typography>
              <Typography sx={{ fontSize: '12px', color: palette.textPrimary, flex: 1, wordBreak: 'break-word' }}>{item}</Typography>
              {!readOnly && (
                <IconButton
                  className="list-remove-btn"
                  size="small"
                  onClick={() => handleRemove(idx)}
                  sx={{ p: '2px', color: palette.textSecondary, opacity: 0, transition: 'opacity 0.15s', '&:hover': { color: palette.errorText || '#b91c1c' } }}
                >
                  <CloseIcon sx={{ width: 14, height: 14 }} />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}
      {!readOnly && (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <TextField inputRef={inputRef} size="small" fullWidth placeholder={placeholder || 'Type an item and press Enter...'} onKeyDown={handleKeyDown} error={hasError} />
          <Button variant="outlined" size="small" onClick={handleAdd} sx={{ textTransform: 'none', fontSize: '12px', flexShrink: 0 }} startIcon={<AddIcon sx={{ width: 14, height: 14 }} />}>
            Add
          </Button>
        </Box>
      )}
    </Box>
  );
}

// ══════════════════════════════════════════
// REPEATABLE SECTION
// ══════════════════════════════════════════

function RepeatableSectionRenderer({
  field, value, onChange, onFieldBlur, readOnly, palette, theme, isDark,
  displayContentSx, errors, warnings, prefillState, onUpload, onRemoveFile, retryUpload, uploadingFieldKey, publicMode,
}) {
  const rows = Array.isArray(value) ? value : [{}];
  const children = field.children || [];
  const minReps = field.minRepetitions ?? 1;
  const maxReps = field.maxRepetitions;
  const canRemove = rows.length > minReps;
  const canAdd = !maxReps || rows.length < maxReps;
  const sectionKey = field.key;

  const handleChildChange = (rowIdx, childKey, childValue) => {
    const updated = rows.map((row, i) => i === rowIdx ? { ...row, [childKey]: childValue } : row);
    onChange(sectionKey, updated);
  };

  const handleChildBlur = (rowIdx, childKey, childValue) => {
    const updated = rows.map((row, i) => i === rowIdx ? { ...row, [childKey]: childValue } : row);
    onFieldBlur?.(sectionKey, updated);
  };

  const handleAddRow = () => { if (canAdd) onChange(sectionKey, [...rows, {}]); };
  const handleRemoveRow = (rowIdx) => {
    if (!canRemove) return;
    const updated = rows.filter((_, i) => i !== rowIdx);
    onChange(sectionKey, updated);
    onFieldBlur?.(sectionKey, updated);
  };

  const FieldLabelComp = publicMode ? PublicFieldLabel : ClassicFieldLabel;
  const EditorWrapper = publicMode ? RichTextEditorWrapper : ClassicRichTextEditorWrapper;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: publicMode ? 1.5 : 1 }}>
      {rows.map((row, rowIdx) => (
        <Box
          key={rowIdx}
          sx={{
            borderRadius: publicMode ? '10px' : '8px',
            border: `1px solid ${publicMode ? palette.inputBorder : palette.border}`,
            backgroundColor: publicMode ? 'transparent' : (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'),
            overflow: 'hidden',
          }}
        >
          {/* Row header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: publicMode ? 2 : 1.25,
              py: publicMode ? 1 : 0.5,
              borderBottom: `1px solid ${publicMode ? palette.inputBorder : palette.border}`,
              backgroundColor: publicMode ? 'transparent' : (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
              ...(publicMode ? { '&:hover .remove-row-btn': { opacity: 1 } } : {}),
            }}
          >
            {publicMode ? (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '6px', bgcolor: palette.chipBg, fontSize: '12px', fontWeight: 600, color: palette.chipText }}>
                {rowIdx + 1}
              </Box>
            ) : (
              <Typography sx={{ fontSize: '11px', fontWeight: 600, color: palette.textSecondary }}>#{rowIdx + 1}</Typography>
            )}
            {!readOnly && canRemove && (
              <IconButton
                className={publicMode ? 'remove-row-btn' : undefined}
                size="small"
                onClick={() => handleRemoveRow(rowIdx)}
                sx={{
                  p: publicMode ? '4px' : '2px',
                  color: palette.textSecondary,
                  ...(publicMode ? { opacity: 0, transition: 'opacity 150ms ease' } : {}),
                  '&:hover': { color: palette.errorText || '#b91c1c' },
                }}
              >
                <DeleteOutlineIcon sx={{ width: publicMode ? 16 : 14, height: publicMode ? 16 : 14 }} />
              </IconButton>
            )}
          </Box>

          {/* Row fields */}
          <Box sx={{ p: publicMode ? 2 : 1.25, display: 'flex', flexDirection: 'column', gap: publicMode ? 2.5 : 1.25 }}>
            {children.map((childField) => {
              const childKey = childField.key;
              const childType = String(childField.type || '');
              const isDisplay = childType === 'display';
              const childValue = isDisplay
                ? ''
                : childType === 'checkbox' || childType === 'list' || childType.endsWith('_upload')
                  ? toArray(row[childKey])
                  : (row[childKey] ?? '');
              const errKey = `${sectionKey}[${rowIdx}].${childKey}`;
              const childErr = errors?.[errKey];
              const childWarn = warnings?.[errKey];
              const childHasError = Boolean(childErr);

              return (
                <Box key={childKey} sx={{ display: 'flex', flexDirection: 'column', gap: publicMode ? 0 : 0.5 }}>
                  <FieldLabelComp label={childField.label || childKey} required={childField.required} isDisplay={isDisplay} palette={palette} />

                  {isDisplay && <DisplayContent field={childField} displayContentSx={displayContentSx} />}

                  {/* Text input */}
                  {childType === 'text' && (publicMode ? (
                    <StyledTextField palette={palette} hasError={childHasError} value={childValue} disabled={readOnly} multiline={Boolean(childField.multiline)} minRows={childField.multiline ? 4 : undefined} placeholder={childField.placeholder || ''} variant="outlined" onChange={(e) => handleChildChange(rowIdx, childKey, e.target.value)} onBlur={(e) => handleChildBlur(rowIdx, childKey, e.target.value)} />
                  ) : (
                    <TextField size="small" fullWidth value={childValue} disabled={readOnly} multiline={Boolean(childField.multiline)} minRows={childField.multiline ? 3 : 1} placeholder={childField.placeholder || ''} onChange={(e) => handleChildChange(rowIdx, childKey, e.target.value)} onBlur={(e) => handleChildBlur(rowIdx, childKey, e.target.value)} error={childHasError} />
                  ))}

                  {/* Number */}
                  {childType === 'number' && (publicMode ? (
                    <StyledTextField palette={palette} hasError={childHasError} type="number" value={childValue} disabled={readOnly} variant="outlined" onChange={(e) => handleChildChange(rowIdx, childKey, e.target.value)} onBlur={(e) => handleChildBlur(rowIdx, childKey, e.target.value)} />
                  ) : (
                    <TextField size="small" fullWidth type="number" value={childValue} disabled={readOnly} onChange={(e) => handleChildChange(rowIdx, childKey, e.target.value)} onBlur={(e) => handleChildBlur(rowIdx, childKey, e.target.value)} error={childHasError} />
                  ))}

                  {/* Date */}
                  {childType === 'date' && (publicMode ? (
                    <StyledTextField palette={palette} hasError={childHasError} type="date" value={childValue} disabled={readOnly} variant="outlined" onChange={(e) => handleChildChange(rowIdx, childKey, e.target.value)} onBlur={(e) => handleChildBlur(rowIdx, childKey, e.target.value)} />
                  ) : (
                    <TextField size="small" fullWidth type="date" value={childValue} disabled={readOnly} onChange={(e) => handleChildChange(rowIdx, childKey, e.target.value)} onBlur={(e) => handleChildBlur(rowIdx, childKey, e.target.value)} error={childHasError} />
                  ))}

                  {/* Time */}
                  {childType === 'time' && (publicMode ? (
                    <StyledTextField palette={palette} hasError={childHasError} type="time" value={childValue} disabled={readOnly} variant="outlined" onChange={(e) => handleChildChange(rowIdx, childKey, e.target.value)} onBlur={(e) => handleChildBlur(rowIdx, childKey, e.target.value)} />
                  ) : (
                    <TextField size="small" fullWidth type="time" value={childValue} disabled={readOnly} onChange={(e) => handleChildChange(rowIdx, childKey, e.target.value)} onBlur={(e) => handleChildBlur(rowIdx, childKey, e.target.value)} error={childHasError} />
                  ))}

                  {/* Dropdown */}
                  {childType === 'dropdown' && (() => {
                    const opts = dropdownOptions(childField.options);
                    return publicMode ? (
                      <StyledSelectField palette={palette} hasError={childHasError} select value={childValue} disabled={readOnly} variant="outlined" onChange={(e) => handleChildChange(rowIdx, childKey, e.target.value)} onBlur={(e) => handleChildBlur(rowIdx, childKey, e.target.value)}
                        SelectProps={{ displayEmpty: true, renderValue: (v) => { if (!v) return <Typography component="span" sx={{ color: '#a1a1aa' }}>Select an option...</Typography>; const match = opts.find((o) => o.value === v); return match ? (match.label || match.value) : v; } }}
                      >
                        {opts.map((opt) => (<MenuItem key={String(opt.value)} value={opt.value}>{opt.label || opt.value}</MenuItem>))}
                      </StyledSelectField>
                    ) : (
                      <TextField size="small" fullWidth select value={childValue} disabled={readOnly} onChange={(e) => handleChildChange(rowIdx, childKey, e.target.value)} onBlur={(e) => handleChildBlur(rowIdx, childKey, e.target.value)} error={childHasError}>
                        {opts.map((opt) => (<MenuItem key={String(opt.value)} value={opt.value}>{opt.label || opt.value}</MenuItem>))}
                      </TextField>
                    );
                  })()}

                  {/* Radio */}
                  {childType === 'radio' && (publicMode ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {dropdownOptions(childField.options).map((opt) => {
                        const selected = childValue === opt.value;
                        return (
                          <StyledRadioOption key={String(opt.value)} palette={palette} selected={selected} onClick={() => { if (!readOnly) { handleChildChange(rowIdx, childKey, opt.value); handleChildBlur(rowIdx, childKey, opt.value); } }}>
                            <StyledRadio palette={palette} checked={selected} disabled={readOnly} value={opt.value} name={`${sectionKey}-${rowIdx}-${childKey}`} disableRipple />
                            <Typography sx={{ fontSize: '14px', color: palette.textPrimary }}>{opt.label || opt.value}</Typography>
                          </StyledRadioOption>
                        );
                      })}
                    </Box>
                  ) : (
                    <RadioGroup value={childValue} onChange={(e) => { handleChildChange(rowIdx, childKey, e.target.value); handleChildBlur(rowIdx, childKey, e.target.value); }}>
                      {dropdownOptions(childField.options).map((opt) => (
                        <FormControlLabel key={String(opt.value)} value={opt.value} control={<Radio size="small" disabled={readOnly} />} label={<Typography sx={{ fontSize: '12px' }}>{opt.label || opt.value}</Typography>} />
                      ))}
                    </RadioGroup>
                  ))}

                  {/* Checkbox */}
                  {childType === 'checkbox' && (publicMode ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {dropdownOptions(childField.options).map((opt) => {
                        const sel = Array.isArray(childValue) ? childValue : [];
                        const checked = sel.includes(opt.value);
                        return (
                          <StyledCheckboxOption key={String(opt.value)} palette={palette} selected={checked} onClick={(e) => { if (readOnly) return; e.preventDefault(); const set = new Set(sel); if (checked) set.delete(opt.value); else set.add(opt.value); const next = Array.from(set); handleChildChange(rowIdx, childKey, next); handleChildBlur(rowIdx, childKey, next); }}>
                            <StyledCheckbox palette={palette} checked={checked} disabled={readOnly} disableRipple />
                            <Typography sx={{ fontSize: '14px', color: palette.textPrimary }}>{opt.label || opt.value}</Typography>
                          </StyledCheckboxOption>
                        );
                      })}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      {dropdownOptions(childField.options).map((opt) => {
                        const sel = Array.isArray(childValue) ? childValue : [];
                        const checked = sel.includes(opt.value);
                        return (
                          <FormControlLabel key={String(opt.value)} control={<Checkbox size="small" disabled={readOnly} checked={checked} onChange={(e) => { const set = new Set(sel); if (e.target.checked) set.add(opt.value); else set.delete(opt.value); const next = Array.from(set); handleChildChange(rowIdx, childKey, next); handleChildBlur(rowIdx, childKey, next); }} />} label={<Typography sx={{ fontSize: '12px' }}>{opt.label || opt.value}</Typography>} />
                        );
                      })}
                    </Box>
                  ))}

                  {/* Rich text */}
                  {childType === 'rich_text' && (
                    <EditorWrapper>
                      <TipTapEditor value={childValue} onChange={(val) => handleChildChange(rowIdx, childKey, val)} onBlur={(html) => handleChildBlur(rowIdx, childKey, html)} showToolbar={!readOnly} stripHtml={false} placeholder={childField.placeholder || 'Enter formatted text...'} minHeight="80px" readOnly={readOnly} />
                    </EditorWrapper>
                  )}

                  {/* List */}
                  {childType === 'list' && (
                    <ListFieldRenderer fieldKey={childKey} value={childValue} onChange={(_, next) => handleChildChange(rowIdx, childKey, next)} onFieldBlur={(_, next) => handleChildBlur(rowIdx, childKey, next)} readOnly={readOnly} placeholder={childField.placeholder} palette={palette} hasError={childHasError} publicMode={publicMode} />
                  )}

                  {/* Upload */}
                  {childType.endsWith('_upload') && (publicMode ? (
                    <Box>
                      {childValue.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, pt: 0.75 }}>
                          {childValue.map((file, fIdx) => {
                            const att = fileAttachment(file, fIdx);
                            const upKey = `${sectionKey}[${rowIdx}].${childKey}`;
                            return (
                              <FileTile
                                key={`${childKey}-${rowIdx}-${att.id}`}
                                attachment={att}
                                onRemove={readOnly ? undefined : () => {
                                  if (onRemoveFile) onRemoveFile(upKey, att.id);
                                  else {
                                    const next = childValue.filter((_, i) => i !== fIdx);
                                    handleChildChange(rowIdx, childKey, next);
                                    handleChildBlur(rowIdx, childKey, next);
                                  }
                                }}
                                onRetry={file?.error && retryUpload ? () => retryUpload(upKey, att.id) : undefined}
                              />
                            );
                          })}
                        </Box>
                      )}
                      {!readOnly && onUpload && (() => {
                        const upKey = `${sectionKey}[${rowIdx}].${childKey}`;
                        const maxFiles = childField.upload?.maxFiles;
                        const anyUploading = childValue.some((f) => f?.uploading);
                        return (
                          <UploadZone
                            palette={palette}
                            inputId={`upload-${sectionKey}-${rowIdx}-${childKey}`}
                            isUploading={uploadingFieldKey === upKey || anyUploading}
                            isMaxed={maxFiles && childValue.length >= maxFiles}
                            multiple={(maxFiles || 1) > 1}
                            accept={(childField.upload?.allowedMimeTypes || []).join(',')}
                            iconSize={24}
                            fontSize="13px"
                            maxedLabel={`Max ${maxFiles} file${maxFiles > 1 ? 's' : ''} reached`}
                            onFiles={(files) => {
                              const remaining = maxFiles ? maxFiles - childValue.length : Infinity;
                              onUpload(upKey, files.slice(0, remaining));
                            }}
                          />
                        );
                      })()}
                    </Box>
                  ) : (
                    <Box sx={{ border: `1px dashed ${palette.border}`, borderRadius: '8px', p: 1 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: childValue.length ? 1 : 0, pt: childValue.length ? 0.75 : 0 }}>
                        {childValue.map((file, fIdx) => {
                          const att = fileAttachment(file, fIdx);
                          const upKey = `${sectionKey}[${rowIdx}].${childKey}`;
                          return (
                            <FileTile
                              key={`${childKey}-${rowIdx}-${att.id}`}
                              attachment={att}
                              onRemove={readOnly ? undefined : () => {
                                if (onRemoveFile) onRemoveFile(upKey, att.id);
                                else {
                                  const next = childValue.filter((_, i) => i !== fIdx);
                                  handleChildChange(rowIdx, childKey, next);
                                  handleChildBlur(rowIdx, childKey, next);
                                }
                              }}
                              onRetry={file?.error && retryUpload ? () => retryUpload(upKey, att.id) : undefined}
                            />
                          );
                        })}
                      </Box>
                      {!readOnly && onUpload && (
                        <Box>
                          <input id={`upload-${sectionKey}-${rowIdx}-${childKey}`} type="file" style={{ display: 'none' }} multiple={(childField.upload?.maxFiles || 1) > 1} accept={(childField.upload?.allowedMimeTypes || []).join(',')} onChange={(e) => { const maxFiles = childField.upload?.maxFiles; const remaining = maxFiles ? maxFiles - childValue.length : Infinity; const files = Array.from(e.target.files || []).slice(0, remaining); onUpload(`${sectionKey}[${rowIdx}].${childKey}`, files); e.target.value = ''; }} />
                          <Button variant="outlined" size="small" component="label" htmlFor={`upload-${sectionKey}-${rowIdx}-${childKey}`} disabled={uploadingFieldKey === `${sectionKey}[${rowIdx}].${childKey}` || (childField.upload?.maxFiles && childValue.length >= childField.upload.maxFiles)} sx={{ textTransform: 'none', fontSize: '12px' }}>
                            {uploadingFieldKey === `${sectionKey}[${rowIdx}].${childKey}` ? 'Uploading...' : childField.upload?.maxFiles && childValue.length >= childField.upload.maxFiles ? `Max ${childField.upload.maxFiles} file${childField.upload.maxFiles > 1 ? 's' : ''} reached` : 'Upload file'}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ))}

                  {publicMode ? (
                    <PublicFieldFeedback err={childErr} warn={childWarn} helpText={childField.helpText} palette={palette} isDark={isDark} />
                  ) : (
                    <ClassicFieldFeedback err={childErr} warn={childWarn} helpText={childField.helpText} label={childField.label || childKey} palette={palette} isDark={isDark} />
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}

      {!readOnly && canAdd && (publicMode ? (
        <GhostAddButton palette={palette} onClick={handleAddRow}>
          <AddIcon sx={{ width: 16, height: 16 }} />
          Add {field.label || 'entry'}
        </GhostAddButton>
      ) : (
        <Button variant="outlined" size="small" onClick={handleAddRow} sx={{ textTransform: 'none', fontSize: '12px', alignSelf: 'flex-start' }} startIcon={<AddIcon sx={{ width: 14, height: 14 }} />}>
          Add {field.label || 'entry'}
        </Button>
      ))}
    </Box>
  );
}

// ══════════════════════════════════════════
// MAIN RENDERER
// ══════════════════════════════════════════

/**
 * @typedef {Object} DynamicFormRendererProps
 * @property {Object} form                          Authored form data ({title, description, fields}).
 * @property {Object} answers                       Current respondent answers, keyed by field.key.
 * @property {(key: string, value: any) => void} onChange          Required for editable fields.
 * @property {((key: string, value: any) => void) | null} onFieldBlur
 *   Optional blur handler (server validation, autosave). Pass null explicitly to opt out.
 * @property {{errors: Object, warnings: Object}} validation
 * @property {boolean=} readOnly
 * @property {((fieldKey: string, filesOrFile: File|File[]) => Promise<void>) | null} onUpload
 *   Required if the form has any file_upload field. Pass null explicitly to opt out.
 * @property {((fieldKey: string, fileId: string) => Promise<void>) | null} onRemoveFile
 *   Paired with onUpload. Pass null explicitly to opt out of server-side delete.
 * @property {((fieldKey: string, placeholderId: string) => Promise<void>) | null} retryUpload
 *   Paired with onUpload. Pass null explicitly to opt out of retry UX for failed uploads.
 * @property {string|null=} uploadingFieldKey       Editor-side (DynamicFormEditor preview) legacy prop.
 * @property {boolean=} publicMode
 */

function hasUploadField(fields) {
  return Array.isArray(fields) && fields.some((f) => typeof f?.type === 'string' && f.type.endsWith('_upload'));
}

function hasEditableField(fields) {
  if (!Array.isArray(fields)) return false;
  return fields.some((f) => typeof f?.type === 'string' && f.type !== 'display');
}

export default function DynamicFormRenderer(props) {
  const {
    form,
    answers,
    onChange,
    validation,
    readOnly = false,
    publicMode = false,
    onFieldBlur = null,
    uploadingFieldKey = null,
  } = props;
  // Normalize handler props so explicit `undefined` (developer forgot to pass) is
  // distinguishable from explicit `null` (opt-out). The dev-only useEffect below warns
  // on undefined; both undefined and null are rendered the same in prod.
  const onUpload = props.onUpload === undefined ? null : props.onUpload;
  const onRemoveFile = props.onRemoveFile === undefined ? null : props.onRemoveFile;
  const retryUpload = props.retryUpload === undefined ? null : props.retryUpload;

  // Dev-only: warn when a surface forgets to thread required handlers. Production
  // tolerates silently missing props so a single dropped wire doesn't crash a live
  // respondent — it just degrades to no UI for that interaction.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    const fields = form?.fields;
    if (!Array.isArray(fields) || fields.length === 0) return;
    if (hasUploadField(fields)) {
      if (props.onUpload === undefined) {
        // eslint-disable-next-line no-console
        console.warn(
          'DynamicFormRenderer: form contains a file_upload field but onUpload is '
          + 'undefined. Pass null to opt out explicitly.',
        );
      }
      if (props.onRemoveFile === undefined) {
        // eslint-disable-next-line no-console
        console.warn(
          'DynamicFormRenderer: form contains a file_upload field but onRemoveFile is '
          + 'undefined. Pass null to opt out explicitly.',
        );
      }
    }
    // onChange is required for every editable surface. Skip when the form is
    // display-only (display fields don't consume onChange).
    if (props.onChange === undefined && hasEditableField(fields)) {
      // eslint-disable-next-line no-console
      console.warn(
        'DynamicFormRenderer: onChange is required when rendering editable fields.',
      );
    }
  }, [form, props.onUpload, props.onRemoveFile, props.onChange]);

  const palette = useFormPalette();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const displayContentSx = getDisplayContentSx(palette, isDark, publicMode);
  const fields = normalizeFields(form);
  const prefillState = form?.prefillState || {};
  const errors = validation?.errors || {};
  const warnings = validation?.warnings || {};

  const FieldLabelComp = publicMode ? PublicFieldLabel : ClassicFieldLabel;
  const FeedbackComp = publicMode ? PublicFieldFeedback : ClassicFieldFeedback;
  const EditorWrapper = publicMode ? RichTextEditorWrapper : ClassicRichTextEditorWrapper;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: publicMode ? 0 : 1.5 }}>
      {fields.map((field, fieldIndex) => {
        const fieldType = String(field.type || '');
        const isDisplayField = fieldType === 'display';
        const key = field.key;
        const label = field.label || key;
        const value = valueForField(field, answers);
        const err = isDisplayField ? null : errors[key];
        const warn = isDisplayField ? null : warnings[key];
        const prefill = prefillState[key];
        const hasError = Boolean(err);
        const hasWarning = !hasError && Boolean(warn);

        // ── Public mode: animated, cardless fields ──
        if (publicMode) {
          return (
            <AnimatedFieldWrapper
              key={key}
              id={`field-${key}`}
              index={fieldIndex}
              palette={palette}
              sx={{
                px: 1,
                borderBottom: fieldIndex < fields.length - 1
                  ? `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`
                  : 'none',
              }}
            >
              {prefill && <PublicPrefillChip palette={palette} />}
              {!isDisplayField && <FieldLabelComp label={label} required={field.required} isDisplay={isDisplayField} palette={palette} />}

              {isDisplayField && <DisplayContent field={field} displayContentSx={displayContentSx} />}

              {fieldType === 'text' && (
                <StyledTextField palette={palette} hasError={hasError} value={value} disabled={readOnly} multiline={Boolean(field.multiline)} minRows={field.multiline ? 4 : undefined} placeholder={field.placeholder || ''} variant="outlined" onChange={(e) => onChange(key, e.target.value)} onBlur={(e) => onFieldBlur?.(key, e.target.value)} />
              )}

              {fieldType === 'number' && (
                <StyledTextField palette={palette} hasError={hasError} type="number" value={value} disabled={readOnly} variant="outlined" onChange={(e) => onChange(key, e.target.value)} onBlur={(e) => onFieldBlur?.(key, e.target.value)} />
              )}

              {fieldType === 'date' && (
                <StyledTextField palette={palette} hasError={hasError} type="date" value={value} disabled={readOnly} variant="outlined" onChange={(e) => onChange(key, e.target.value)} onBlur={(e) => onFieldBlur?.(key, e.target.value)} />
              )}

              {fieldType === 'time' && (
                <StyledTextField palette={palette} hasError={hasError} type="time" value={value} disabled={readOnly} variant="outlined" onChange={(e) => onChange(key, e.target.value)} onBlur={(e) => onFieldBlur?.(key, e.target.value)} />
              )}

              {fieldType === 'dropdown' && (() => {
                const opts = dropdownOptions(field.options);
                return (
                  <StyledSelectField palette={palette} hasError={hasError} select value={value} disabled={readOnly} variant="outlined" onChange={(e) => onChange(key, e.target.value)} onBlur={(e) => onFieldBlur?.(key, e.target.value)}
                    SelectProps={{ displayEmpty: true, renderValue: (v) => { if (!v) return <Typography component="span" sx={{ color: '#a1a1aa' }}>Select an option...</Typography>; const match = opts.find((o) => o.value === v); return match ? (match.label || match.value) : v; } }}
                  >
                    {opts.map((option) => (<MenuItem key={String(option.value)} value={option.value}>{option.label || option.value}</MenuItem>))}
                  </StyledSelectField>
                );
              })()}

              {fieldType === 'radio' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {dropdownOptions(field.options).map((option) => {
                    const selected = value === option.value;
                    return (
                      <StyledRadioOption key={String(option.value)} palette={palette} selected={selected} onClick={() => { if (!readOnly) { onChange(key, option.value); onFieldBlur?.(key, option.value); } }}>
                        <StyledRadio palette={palette} checked={selected} disabled={readOnly} value={option.value} name={key} disableRipple />
                        <Typography sx={{ fontSize: '14px', color: palette.textPrimary }}>{option.label || option.value}</Typography>
                      </StyledRadioOption>
                    );
                  })}
                </Box>
              )}

              {fieldType === 'checkbox' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {dropdownOptions(field.options).map((option) => {
                    const checked = value.includes(option.value);
                    return (
                      <StyledCheckboxOption key={String(option.value)} palette={palette} selected={checked} onClick={(e) => { if (readOnly) return; e.preventDefault(); const set = new Set(value); if (checked) set.delete(option.value); else set.add(option.value); const nextValue = Array.from(set); onChange(key, nextValue); onFieldBlur?.(key, nextValue); }}>
                        <StyledCheckbox palette={palette} checked={checked} disabled={readOnly} disableRipple />
                        <Typography sx={{ fontSize: '14px', color: palette.textPrimary }}>{option.label || option.value}</Typography>
                      </StyledCheckboxOption>
                    );
                  })}
                </Box>
              )}

              {fieldType.endsWith('_upload') && (
                <Box>
                  {value.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, pt: 0.75 }}>
                      {value.map((file, idx) => {
                        const att = fileAttachment(file, idx);
                        return (
                          <FileTile
                            key={`${key}-${att.id}`}
                            attachment={att}
                            onRemove={readOnly ? undefined : () => {
                              if (onRemoveFile) onRemoveFile(key, att.id);
                              else {
                                const next = value.filter((_, i) => i !== idx);
                                onChange(key, next);
                                onFieldBlur?.(key, next);
                              }
                            }}
                            onRetry={file?.error && retryUpload ? () => retryUpload(key, att.id) : undefined}
                          />
                        );
                      })}
                    </Box>
                  )}
                  {!readOnly && onUpload && (() => {
                    const maxFiles = field.upload?.maxFiles;
                    const anyUploading = value.some((f) => f?.uploading);
                    return (
                      <UploadZone
                        palette={palette}
                        inputId={`upload-${key}`}
                        isUploading={uploadingFieldKey === key || anyUploading}
                        isMaxed={maxFiles && value.length >= maxFiles}
                        multiple={(maxFiles || 1) > 1}
                        accept={(field.upload?.allowedMimeTypes || []).join(',')}
                        iconSize={28}
                        fontSize="14px"
                        maxedLabel={`Max ${maxFiles} file${maxFiles > 1 ? 's' : ''} reached`}
                        onFiles={(files) => {
                          const remaining = maxFiles ? maxFiles - value.length : Infinity;
                          onUpload(key, files.slice(0, remaining));
                        }}
                      />
                    );
                  })()}
                </Box>
              )}

              {fieldType === 'rich_text' && (
                <EditorWrapper>
                  <TipTapEditor value={value} onChange={(val) => onChange(key, val)} onBlur={(html) => onFieldBlur?.(key, html)} showToolbar={!readOnly} stripHtml={false} placeholder={field.placeholder || 'Enter formatted text...'} minHeight="120px" readOnly={readOnly} />
                </EditorWrapper>
              )}

              {fieldType === 'list' && (
                <ListFieldRenderer fieldKey={key} value={value} onChange={onChange} onFieldBlur={onFieldBlur} readOnly={readOnly} placeholder={field.placeholder} palette={palette} hasError={hasError} publicMode={publicMode} />
              )}

              {fieldType === 'repeatable_section' && (
                <RepeatableSectionRenderer field={field} value={value} onChange={onChange} onFieldBlur={onFieldBlur} readOnly={readOnly} palette={palette} theme={theme} isDark={isDark} displayContentSx={displayContentSx} errors={errors} warnings={warnings} prefillState={prefillState} onUpload={onUpload} onRemoveFile={onRemoveFile} retryUpload={retryUpload} uploadingFieldKey={uploadingFieldKey} publicMode={publicMode} />
              )}

              <PublicFieldFeedback err={err} warn={warn} helpText={field.helpText} palette={palette} isDark={isDark} />
            </AnimatedFieldWrapper>
          );
        }

        // ── Classic mode: bordered cards (original look) ──
        const borderColor = hasError
          ? (isDark ? 'rgba(185,28,28,0.5)' : '#fca5a5')
          : hasWarning
            ? (isDark ? 'rgba(161,98,7,0.5)' : '#fcd34d')
            : palette.border;
        const backgroundColor = hasError
          ? (isDark ? 'rgba(185,28,28,0.1)' : '#fff7f7')
          : hasWarning
            ? (isDark ? 'rgba(161,98,7,0.1)' : '#fffcf1')
            : palette.surface;

        return (
          <Box
            key={key}
            id={`field-${key}`}
            sx={{
              border: `1px solid ${borderColor}`,
              borderRadius: '10px',
              p: 1.25,
              backgroundColor,
              transition: 'border-color 120ms ease, background-color 120ms ease',
            }}
          >
            {prefill && <ClassicPrefillChip isDark={isDark} />}
            {!isDisplayField && <FieldLabelComp label={label} required={field.required} isDisplay={isDisplayField} palette={palette} />}

            {isDisplayField && <DisplayContent field={field} displayContentSx={displayContentSx} />}

            {fieldType === 'text' && (
              <TextField size="small" fullWidth value={value} disabled={readOnly} multiline={Boolean(field.multiline)} minRows={field.multiline ? 4 : 1} placeholder={field.placeholder || ''} onChange={(e) => onChange(key, e.target.value)} onBlur={(e) => onFieldBlur?.(key, e.target.value)} error={Boolean(err)} />
            )}

            {fieldType === 'number' && (
              <TextField size="small" fullWidth type="number" value={value} disabled={readOnly} onChange={(e) => onChange(key, e.target.value)} onBlur={(e) => onFieldBlur?.(key, e.target.value)} error={Boolean(err)} />
            )}

            {fieldType === 'date' && (
              <TextField size="small" fullWidth type="date" value={value} disabled={readOnly} onChange={(e) => onChange(key, e.target.value)} onBlur={(e) => onFieldBlur?.(key, e.target.value)} error={Boolean(err)} />
            )}

            {fieldType === 'time' && (
              <TextField size="small" fullWidth type="time" value={value} disabled={readOnly} onChange={(e) => onChange(key, e.target.value)} onBlur={(e) => onFieldBlur?.(key, e.target.value)} error={Boolean(err)} />
            )}

            {fieldType === 'dropdown' && (
              <TextField size="small" fullWidth select value={value} disabled={readOnly} onChange={(e) => onChange(key, e.target.value)} onBlur={(e) => onFieldBlur?.(key, e.target.value)} error={Boolean(err)}>
                {dropdownOptions(field.options).map((option) => (<MenuItem key={String(option.value)} value={option.value}>{option.label || option.value}</MenuItem>))}
              </TextField>
            )}

            {fieldType === 'radio' && (
              <RadioGroup value={value} onChange={(e) => { onChange(key, e.target.value); onFieldBlur?.(key, e.target.value); }}>
                {dropdownOptions(field.options).map((option) => (
                  <FormControlLabel key={String(option.value)} value={option.value} control={<Radio size="small" disabled={readOnly} />} label={<Typography sx={{ fontSize: '12px' }}>{option.label || option.value}</Typography>} />
                ))}
              </RadioGroup>
            )}

            {fieldType === 'checkbox' && (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {dropdownOptions(field.options).map((option) => {
                  const checked = value.includes(option.value);
                  return (
                    <FormControlLabel key={String(option.value)} control={<Checkbox size="small" disabled={readOnly} checked={checked} onChange={(e) => { const set = new Set(value); if (e.target.checked) set.add(option.value); else set.delete(option.value); const nextValue = Array.from(set); onChange(key, nextValue); onFieldBlur?.(key, nextValue); }} />} label={<Typography sx={{ fontSize: '12px' }}>{option.label || option.value}</Typography>} />
                  );
                })}
              </Box>
            )}

            {fieldType.endsWith('_upload') && (
              <Box sx={{ border: `1px dashed ${palette.border}`, borderRadius: '8px', p: 1 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: value.length ? 1 : 0, pt: value.length ? 0.75 : 0 }}>
                  {value.map((file, idx) => {
                    const att = fileAttachment(file, idx);
                    return (
                      <FileTile
                        key={`${key}-${att.id}`}
                        attachment={att}
                        onRemove={readOnly ? undefined : () => {
                          if (onRemoveFile) onRemoveFile(key, att.id);
                          else {
                            const next = value.filter((_, i) => i !== idx);
                            onChange(key, next);
                            onFieldBlur?.(key, next);
                          }
                        }}
                        onRetry={file?.error && retryUpload ? () => retryUpload(key, att.id) : undefined}
                      />
                    );
                  })}
                </Box>
                {!readOnly && onUpload && (
                  <Box>
                    <input id={`upload-${key}`} type="file" style={{ display: 'none' }} multiple={(field.upload?.maxFiles || 1) > 1} accept={(field.upload?.allowedMimeTypes || []).join(',')} onChange={(e) => { const maxFiles = field.upload?.maxFiles; const remaining = maxFiles ? maxFiles - value.length : Infinity; const files = Array.from(e.target.files || []).slice(0, remaining); onUpload(key, files); e.target.value = ''; }} />
                    <Button variant="outlined" size="small" component="label" htmlFor={`upload-${key}`} disabled={uploadingFieldKey === key || (field.upload?.maxFiles && value.length >= field.upload.maxFiles)} sx={{ textTransform: 'none', fontSize: '12px' }}>
                      {uploadingFieldKey === key ? 'Uploading...' : field.upload?.maxFiles && value.length >= field.upload.maxFiles ? `Max ${field.upload.maxFiles} file${field.upload.maxFiles > 1 ? 's' : ''} reached` : 'Upload file'}
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {fieldType === 'rich_text' && (
              <EditorWrapper>
                <TipTapEditor value={value} onChange={(val) => onChange(key, val)} onBlur={(html) => onFieldBlur?.(key, html)} showToolbar={!readOnly} stripHtml={false} placeholder={field.placeholder || 'Enter formatted text...'} minHeight="120px" readOnly={readOnly} />
              </EditorWrapper>
            )}

            {fieldType === 'list' && (
              <ListFieldRenderer fieldKey={key} value={value} onChange={onChange} onFieldBlur={onFieldBlur} readOnly={readOnly} placeholder={field.placeholder} palette={palette} hasError={hasError} publicMode={publicMode} />
            )}

            {fieldType === 'repeatable_section' && (
              <RepeatableSectionRenderer field={field} value={value} onChange={onChange} onFieldBlur={onFieldBlur} readOnly={readOnly} palette={palette} theme={theme} isDark={isDark} displayContentSx={displayContentSx} errors={errors} warnings={warnings} prefillState={prefillState} onUpload={onUpload} onRemoveFile={onRemoveFile} retryUpload={retryUpload} uploadingFieldKey={uploadingFieldKey} publicMode={publicMode} />
            )}

            <ClassicFieldFeedback err={err} warn={warn} helpText={field.helpText} label={label} palette={palette} isDark={isDark} />
          </Box>
        );
      })}
    </Box>
  );
}
